import React, { useEffect, useRef, useState } from 'react'
import { TourSelection } from '../../components/TourSelection/TourSelection'
import { uploadS3, useAuthStatus, useToken } from '../../utils'
import { updateUserProfileImage } from '../../services/api'
import { Link, useNavigate } from 'react-router-dom'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../store'
import { useSaveSelectedTournaments } from '../../hooks'
import styles from './OnboardDefaultView.module.scss'

export const OnboardDefaultView = () => {
	const { actions, state } = useStateMachine({ updateStore })
	const fileInputRef = useRef(null)
	const [noToursForSelectedGender, setNoToursForSelectedGender] = useState(false)
	const [auth, onChangeAuthStatus] = useAuthStatus()
	const token = useToken()
	const saveMutation = useSaveSelectedTournaments()
	// Store에서 데이터 가져오기
	const profileImage = state.onboardingInput?.profileImage || '/images/Onboard-default-image.png'
	const toursCheckedByChild = state.onboardingInput?.selectedTourIds || []
	const navigate = useNavigate()

	const isNextButtonDisabled = toursCheckedByChild.length === 0 && !noToursForSelectedGender

	const handleImageChangeClick = () => {
		fileInputRef.current.click()
	}

	const handleNoToursAvailable = (isNoTours) => {
		setNoToursForSelectedGender(isNoTours)
	}

	const handleFileChange = async (event) => {
		const file = event.target.files[0]

		if (file) {
			// 1. 파일 확장자 유효성 검사 (JPG, PNG만 허용)
			const allowedTypes = ['image/jpeg', 'image/png']
			if (!allowedTypes.includes(file.type)) {
				alert('JPG 및 PNG 이미지 형식만 허용됩니다.')
				return
			}

			// 2. 파일 크기 유효성 검사 (5MB 이하만 허용)
			const maxSize = 5 * 1024 * 1024
			if (file.size > maxSize) {
				alert('파일 크기는 5MB 이하여야 합니다.')
				return
			}

			const userId = auth.user?.id

			try {
				// S3에 이미지 업로드
				const imageUrl = await uploadS3(userId, 'profile', file)

				if (imageUrl) {
					// Store에 프로필 이미지 저장
					actions.updateStore({
						onboardingInput: {
							...state.onboardingInput,
							profileImage: `${imageUrl}?timestamp=${new Date().getTime()}`,
						},
					})

					// 데이터베이스에 이미지 URL 저장
					await updateUserProfileImage(token, userId, imageUrl)
					console.log(
						'프로필 이미지가 데이터베이스에 성공적으로 저장되었습니다:',
						imageUrl,
					)
				} else {
					console.error('S3 이미지 업로드 실패: imageUrl이 반환되지 않았습니다.')
					throw new Error('Failed to upload image to S3.')
				}
			} catch (error) {
				console.error('이미지 변경 중 오류 발생:', error)
				alert('프로필 이미지 변경 중 오류가 발생했습니다. 다시 시도해주세요.')
			}
		}
	}

	const handleSelectedToursFromChild = (selectedIds) => {
		actions.updateStore({
			onboardingInput: {
				...state.onboardingInput,
				selectedTourIds: selectedIds,
			},
		})
	}

	const handleNextClick = async (e) => {
		e.preventDefault()

		try {
			// 선택된 대회가 있으면 API로 저장
			if (toursCheckedByChild.length > 0) {
				await saveMutation.mutateAsync({
					tournamentIds: toursCheckedByChild,
					skipped: false,
				})
				console.log('선택된 대회가 저장되었습니다:', toursCheckedByChild)
			} else if (noToursForSelectedGender) {
				// 대회가 없는 경우 스킵으로 저장
				await saveMutation.mutateAsync({
					tournamentIds: [],
					skipped: true,
				})
				console.log('대회 선택을 스킵했습니다')
			}

			// 페이지 이동
			navigate('/onboarding/contract-info')
		} catch (error) {
			console.error('대회 저장 실패:', error)
			alert('대회 저장에 실패했습니다. 다시 시도해주세요.')
		}
	}

	return (
		<div className={styles.onboardDefaultContainer}>
			<div className={styles.header}>
				<div className={styles.headerImageContainer}>
					<div className={styles.profileImageContainer}>
						<img
							src={profileImage}
							alt="프로필 이미지"
							className={styles.profileImage}
						/>
					</div>
					<div className={styles.changeImageContainer} onClick={handleImageChangeClick}>
						<img src="/images/pencil-icon.png" className={styles.changeImage} />
					</div>
					<input
						type="file"
						ref={fileInputRef}
						style={{ display: 'none' }}
						accept="image/*"
						onChange={handleFileChange}
					/>
				</div>
				<div className={styles.registCompleteMessage}>
					<div>{auth?.user?.name || '프로'}님</div>
					<div>가입이 완료되었어요!</div>
				</div>
				<div className={styles.contractAnnouncement}>
					<div>
						{' '}
						자세한 정보를 입력해보세요. 자세한 정보를 입력할 수록 더 많은 캐디들과
						매칭될 확률이 높아져요.
					</div>
				</div>
				<div className={styles.contractComment}>
					입력한 내용은 참고용이며 나중에 언제든지 수정이 가능해요.
				</div>
			</div>
			<div className={styles.selectBar}></div>
			<div className={styles.tourCheckMessage}>
				이번년도 출전 예정 대회를 모두 체크해주세요
			</div>

			<div style={{ flex: 1, minHeight: 0, display: 'flex', maxHeight: '800px' }}>
				<TourSelection
					TourCount={false}
					selectOnlyOne={false}
					onSelectedToursChange={handleSelectedToursFromChild}
					onNoToursAvailable={handleNoToursAvailable}
				/>
			</div>

			<div className={styles.processSelector}>
				<Link
					to="/onboarding/contract-info"
					state={{ selectedTourIds: [] }}
					className={`${styles.skip} ${noToursForSelectedGender ? styles.disabledSkip : ''}`}
					onClick={(e) => {
						if (noToursForSelectedGender) {
							e.preventDefault()
						}
					}}>
					Skip
				</Link>
				<Link
					to="/onboarding/contract-info"
					className={`${styles.next} ${isNextButtonDisabled ? styles.disabledNext : ''}`}
					onClick={handleNextClick}>
					다음
				</Link>
			</div>
		</div>
	)
}
